(function () {
  'use strict';

  var INSTALL_GUARD = '__lakohArTryOnPerformanceInstalled__';
  // Shopify and some performance apps can execute deferred assets while
  // document.currentScript points at their loader instead of this asset.
  // Resolve our own tagged script explicitly before setting the global guard;
  // otherwise a false page-type miss would disable the optimizer for the
  // entire page session.
  var ownScript = document.currentScript;
  if (!ownScript || !ownScript.getAttribute || !ownScript.getAttribute('data-lakoh-artryon-page')) {
    ownScript = document.querySelector('script[data-lakoh-artryon-page]');
  }
  var pageType = ownScript && ownScript.getAttribute('data-lakoh-artryon-page');
  if (pageType !== 'product' && pageType !== 'collection' && pageType !== 'search') return;
  if (window[INSTALL_GUARD]) return;
  window[INSTALL_GUARD] = true;

  var SUPPORTED_TYPES = /^(image\/(jpeg|png|webp))$/i;
  var metricSequence = 0;

  function now() {
    return window.performance && performance.now ? performance.now() : Date.now();
  }

  function beginMetric(surface, inputBytes) {
    var id = ++metricSequence;
    var startMark = 'lakoh-artryon-' + surface + '-' + id + '-start';
    if (window.performance && performance.mark) {
      try { performance.mark(startMark); } catch (_) {}
    }
    return { surface: surface, inputBytes: inputBytes, start: now(), startMark: startMark, id: id };
  }

  function finishMetric(metric, outcome, outputBytes) {
    var durationMs = Math.max(0, Math.round((now() - metric.start) * 10) / 10);
    if (window.performance && performance.mark && performance.measure) {
      var endMark = 'lakoh-artryon-' + metric.surface + '-' + metric.id + '-end';
      try {
        performance.mark(endMark);
        performance.measure('lakoh-artryon-' + metric.surface + '-photo-preprocess-' + outcome, metric.startMark, endMark);
        if (performance.clearMarks) {
          performance.clearMarks(metric.startMark);
          performance.clearMarks(endMark);
        }
      } catch (_) {}
    }
    try {
      window.dispatchEvent(new CustomEvent('lakoh:artryon-performance', {
        detail: {
          surface: metric.surface,
          phase: 'photo-preprocess',
          outcome: outcome,
          durationMs: durationMs,
          inputBytes: metric.inputBytes,
          outputBytes: outputBytes
        }
      }));
    } catch (_) {}
  }

  function canvasToBlob(canvas, quality) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (blob) resolve(blob);
        else reject(new Error('Image encoding failed'));
      }, 'image/jpeg', quality);
    });
  }

  async function decodeImage(file, allowImageElementFallback) {
    if (typeof window.createImageBitmap === 'function') {
      try {
        var bitmap = await window.createImageBitmap(file, { imageOrientation: 'from-image' });
        return { source: bitmap, cleanup: function () { if (bitmap.close) bitmap.close(); } };
      } catch (_) {
        if (!allowImageElementFallback) throw _;
      }
    } else if (!allowImageElementFallback) {
      throw new Error('createImageBitmap is unavailable');
    }

    var objectUrl = URL.createObjectURL(file);
    var image = new Image();
    try {
      image.src = objectUrl;
      if (image.decode) {
        await image.decode();
      } else {
        await new Promise(function (resolve, reject) {
          image.onload = resolve;
          image.onerror = reject;
        });
      }
      return { source: image, cleanup: function () { URL.revokeObjectURL(objectUrl); } };
    } catch (error) {
      URL.revokeObjectURL(objectUrl);
      throw error;
    }
  }

  async function transcode(file, config) {
    var decoded = await decodeImage(file, config.allowImageElementFallback);
    try {
      var source = decoded.source;
      var width = source.naturalWidth || source.width;
      var height = source.naturalHeight || source.height;
      if (!width || !height) throw new Error('Image has no dimensions');

      var scale = 1;
      if (config.maxWidth) scale = Math.min(scale, config.maxWidth / width);
      if (config.maxEdge) scale = Math.min(scale, config.maxEdge / Math.max(width, height));
      var targetWidth = Math.max(1, Math.round(width * scale));
      var targetHeight = Math.max(1, Math.round(height * scale));

      var canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      var context = canvas.getContext('2d', { alpha: false });
      if (!context) throw new Error('Canvas 2D context unavailable');
      context.fillStyle = '#fff';
      context.fillRect(0, 0, targetWidth, targetHeight);
      context.drawImage(source, 0, 0, targetWidth, targetHeight);

      var blob = await canvasToBlob(canvas, config.quality);
      canvas.width = canvas.height = 1;
      if (!blob || blob.size >= file.size * config.maxOutputRatio) return null;

      var outputName = file.name || 'try-on-photo.jpg';
      if (config.renameJpeg) outputName = outputName.replace(/\.[^.]*$/, '') + '.jpg';
      return new File([blob], outputName, {
        type: 'image/jpeg',
        lastModified: file.lastModified || Date.now()
      });
    } finally {
      decoded.cleanup();
    }
  }

  function canAttempt(file, minBytes) {
    return Boolean(
      file && file.size >= minBytes && SUPPORTED_TYPES.test(file.type || '') &&
      typeof window.File === 'function' && window.HTMLCanvasElement &&
      typeof HTMLCanvasElement.prototype.toBlob === 'function'
    );
  }

  async function transcodeWithMetric(surface, file, config) {
    var metric = beginMetric(surface, file.size);
    try {
      var result = await transcode(file, config);
      finishMetric(metric, result ? 'optimized' : 'not-smaller', result ? result.size : file.size);
      return result;
    } catch (error) {
      finishMetric(metric, 'failed-fallback', file.size);
      throw error;
    }
  }

  function installProductOptimizer() {
    var WRAPPED_FLAG = '__lakohArTryOnUploadOptimized__';
    var FACE_MATCH_WRAPPED_FLAG = '__lakohArTryOnFaceMatchWrapped__';
    var FACE_CENTER_BOUND_FLAG = '__lakohArTryOnFaceCenterBound__';
    var FACE_IMAGE_SELECTOR = [
      '#tryon_popup_wrapper #model_wrapper > img',
      '#tryon_box #photo_box > img'
    ].join(',');
    var latestSelection = 0;
    var faceCenterSequence = 0;
    var vendorScriptSeen = false;
    var observer;
    var config = {
      maxWidth: 640,
      quality: 0.92,
      maxOutputRatio: 0.95,
      renameJpeg: false,
      allowImageElementFallback: false
    };

    function readTryOnNumber(dataHost, keys) {
      var value;
      for (var i = 0; i < keys.length; i += 1) {
        var key = keys[i];
        if (window.jQuery) {
          try { value = window.jQuery(dataHost).data(key); } catch (_) {}
        }
        if (value == null && dataHost.getAttribute) {
          value = dataHost.getAttribute('data-' + key);
        }
        if (value == null || value === '') continue;
        var numericValue = Number(value);
        if (Number.isFinite(numericValue)) return numericValue;
      }
      return null;
    }

    function getFaceCanvas(image) {
      var dataHost = image && image.parentElement;
      var viewport = dataHost && dataHost.parentElement;
      if (!dataHost || !viewport) return null;

      var currentPopup = dataHost.id === 'model_wrapper' && viewport.id === 'model_frame_wrapper';
      var legacyPopup = dataHost.id === 'photo_box' &&
        viewport.classList && viewport.classList.contains('photo_frame_box');
      return currentPopup || legacyPopup ? { dataHost: dataHost, viewport: viewport } : null;
    }

    function isUploadedPhoto(image) {
      var activeModel = document.querySelector(
        '#tryon_popup_wrapper #model_list li.active, #tryon_box #model_list li.active'
      );
      if (activeModel) {
        return Boolean(activeModel.querySelector(
          '.add_photo_box, .upload_photo_wrapper, .photo_wrapper'
        ));
      }
      // Older templates do not always mark the active thumbnail. Their built-in
      // samples live under /tryon/models/, while uploaded face-detection results
      // use a generated URL.
      return !/\/tryon\/models\//i.test(image.currentSrc || image.src || '');
    }

    function centerDetectedFace(image, expectedSequence) {
      if (expectedSequence !== faceCenterSequence || !image || image.isConnected === false) return false;

      var canvas = getFaceCanvas(image);
      if (!canvas) return false;
      if (!isUploadedPhoto(image)) {
        // Switching back from a tall upload can leave the shared viewport
        // scrolled. Restore the provider's built-in samples to their default
        // top position so a previous user's crop never carries over.
        canvas.viewport.scrollTop = 0;
        return false;
      }
      var dataHost = canvas.dataHost;
      var viewport = canvas.viewport;
      var originalWidth = readTryOnNumber(dataHost, ['original-width', 'model-width']);
      var originalHeight = readTryOnNumber(dataHost, ['original-height', 'model-height']);
      var renderedWidth = image.getBoundingClientRect ? image.getBoundingClientRect().width : image.clientWidth;
      var viewportHeight = viewport.clientHeight;
      if (!(originalWidth > 0 && originalHeight > 0 && renderedWidth > 0 && viewportHeight > 0)) {
        return false;
      }

      // The provider stores uploaded-photo landmarks in jQuery's private data
      // cache. Support both its "landmask" spelling and the data-* attributes
      // used by the current popup template.
      var landmarkYs = [21, 38, 108].map(function (point) {
        return readTryOnNumber(dataHost, [
          'landmask-' + point + '-y',
          'landmark-' + point + '-y'
        ]);
      }).filter(function (value) {
        return value != null && value >= 0 && value <= originalHeight * 1.05;
      }).sort(function (a, b) { return a - b; });
      if (!landmarkYs.length) return false;

      // Use the middle eye/bridge landmark so one noisy point cannot move the
      // face out of view. The photo and frame are children of the same scroll
      // canvas, so scrolling preserves their alignment.
      var faceAnchorY = landmarkYs[Math.floor(landmarkYs.length / 2)];
      var scale = renderedWidth / originalWidth;
      var renderedHeight = originalHeight * scale;
      var maxScroll = Math.max(
        0,
        viewport.scrollHeight - viewportHeight,
        renderedHeight - viewportHeight
      );
      var target = faceAnchorY * scale - viewportHeight * 0.42;
      viewport.scrollTop = Math.round(Math.max(0, Math.min(maxScroll, target)));
      return true;
    }

    function scheduleFaceCentering(image) {
      var expectedSequence = ++faceCenterSequence;
      [0, 60, 250, 550].forEach(function (delay) {
        window.setTimeout(function () {
          centerDetectedFace(image, expectedSequence);
        }, delay);
      });
    }

    function bindFaceImage(image) {
      if (!image || image[FACE_CENTER_BOUND_FLAG]) return;
      Object.defineProperty(image, FACE_CENTER_BOUND_FLAG, { value: true });
      image.addEventListener('load', function () { scheduleFaceCentering(image); });
      if (image.complete) scheduleFaceCentering(image);
    }

    function scheduleCurrentFace() {
      var image = document.querySelector(FACE_IMAGE_SELECTOR);
      if (image) scheduleFaceCentering(image);
    }

    function wrapFaceMatch() {
      var vendorFaceMatch = window.detail_model_frame_match;
      if (typeof vendorFaceMatch !== 'function' || vendorFaceMatch[FACE_MATCH_WRAPPED_FLAG]) return;

      function centeredFaceMatch() {
        var result = vendorFaceMatch.apply(this, arguments);
        if (typeof window.requestAnimationFrame === 'function') {
          window.requestAnimationFrame(function () {
            window.requestAnimationFrame(scheduleCurrentFace);
          });
        } else {
          window.setTimeout(scheduleCurrentFace, 0);
        }
        window.setTimeout(scheduleCurrentFace, 120);
        return result;
      }

      Object.defineProperty(centeredFaceMatch, FACE_MATCH_WRAPPED_FLAG, { value: true });
      Object.defineProperty(centeredFaceMatch, '__vendorFaceMatch__', { value: vendorFaceMatch });
      window.detail_model_frame_match = centeredFaceMatch;
    }

    function inspectFaceNode(node) {
      if (!node || node.nodeType !== 1) return;
      if (node.matches && node.matches(FACE_IMAGE_SELECTOR)) bindFaceImage(node);
      if (node.querySelectorAll) {
        Array.prototype.forEach.call(node.querySelectorAll(FACE_IMAGE_SELECTOR), bindFaceImage);
      }
    }

    function wrapOpenFile() {
      if (!vendorScriptSeen) return;
      var vendorOpenFile = window.openFile;
      if (typeof vendorOpenFile !== 'function' || vendorOpenFile[WRAPPED_FLAG]) return;

      async function optimizedOpenFile(event) {
        var callContext = this;
        var input = event && event.target;
        var file = input && input.files && input.files[0];
        var selection = ++latestSelection;
        if (!canAttempt(file, 1.5 * 1024 * 1024) || typeof window.createImageBitmap !== 'function') {
          return vendorOpenFile.call(callContext, event);
        }

        var loading = document.querySelector('#tryon_popup_wrapper .loading');
        if (loading) loading.style.display = 'block';
        try {
          var optimized = await transcodeWithMetric('product', file, config);
          if (selection !== latestSelection) return;
          if (!optimized) return vendorOpenFile.call(callContext, event);
          return vendorOpenFile.call(callContext, { target: { files: [optimized] } });
        } catch (_) {
          if (selection !== latestSelection) return;
          return vendorOpenFile.call(callContext, event);
        }
      }

      Object.defineProperty(optimizedOpenFile, WRAPPED_FLAG, { value: true });
      Object.defineProperty(optimizedOpenFile, '__vendorOpenFile__', { value: vendorOpenFile });
      window.openFile = optimizedOpenFile;
      wrapFaceMatch();
    }

    function inspectNode(node) {
      if (!node || node.nodeType !== 1) return;
      inspectFaceNode(node);
      var scripts = [];
      if (node.tagName === 'SCRIPT') scripts.push(node);
      if (node.querySelectorAll) scripts = scripts.concat(Array.prototype.slice.call(node.querySelectorAll('script')));
      scripts.forEach(function (script) {
        if (!/\/(?:pc|m)_detail_single\.js(?:\?|$)/.test(script.src || '')) return;
        vendorScriptSeen = true;
        script.addEventListener('load', wrapOpenFile, { once: true });
        script.addEventListener('load', wrapFaceMatch, { once: true });
        window.setTimeout(function () {
          wrapOpenFile();
          wrapFaceMatch();
        }, 0);
      });
    }

    Array.prototype.forEach.call(document.scripts || [], inspectNode);
    inspectFaceNode(document.documentElement);
    // ARTryOn reinserts this script whenever the product popup opens, which
    // overwrites window.openFile. Keep watching so each new vendor function is
    // wrapped after its script load; WRAPPED_FLAG prevents wrapper stacking.
    observer = new MutationObserver(function (records) {
      records.forEach(function (record) {
        Array.prototype.forEach.call(record.addedNodes, inspectNode);
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    // Cached model thumbnails may replace the photo without a reliable load
    // event. Re-run after the provider handles the click; square sample models
    // naturally resolve to scrollTop 0.
    document.addEventListener('click', function (event) {
      var target = event.target;
      var thumbnail = target && target.closest && target.closest(
        '#tryon_popup_wrapper #model_list li, #tryon_box #model_list li'
      );
      if (!thumbnail) return;
      window.setTimeout(function () {
        scheduleCurrentFace();
      }, 0);
    }, true);
  }

  function installCollectionOptimizer() {
    var replaying = new WeakSet();
    var generations = new WeakMap();
    var listenerInstalled = false;
    var vendorScriptSeen = false;
    var observer;
    var config = {
      maxEdge: 1280,
      quality: 0.88,
      maxOutputRatio: 1,
      renameJpeg: true,
      allowImageElementFallback: true
    };

    function activateDesktopListOptimizer() {
      if (!vendorScriptSeen || listenerInstalled) return;
      listenerInstalled = true;

      document.addEventListener('change', function (event) {
        var input = event.target;
        if (!input || input.id !== 'list_imgfile' || input.type !== 'file' || replaying.has(input)) return;
        var file = input.files && input.files[0];
        if (!canAttempt(file, 700 * 1024) || typeof window.DataTransfer !== 'function') return;

        event.preventDefault();
        event.stopImmediatePropagation();
        var generation = (generations.get(input) || 0) + 1;
        generations.set(input, generation);

        transcodeWithMetric('collection', file, config)
          .then(function (optimized) {
            if (generations.get(input) !== generation || !optimized) return;
            var transfer = new DataTransfer();
            transfer.items.add(optimized);
            input.files = transfer.files;
          })
          .catch(function () {})
          .then(function () {
            if (generations.get(input) !== generation) return;
            replaying.add(input);
            try { input.dispatchEvent(new Event('change', { bubbles: true })); }
            finally { replaying.delete(input); }
          });
      }, true);
    }

    function inspectNode(node) {
      if (!node || node.nodeType !== 1) return;
      var scripts = [];
      if (node.tagName === 'SCRIPT') scripts.push(node);
      if (node.querySelectorAll) scripts = scripts.concat(Array.prototype.slice.call(node.querySelectorAll('script')));
      scripts.forEach(function (script) {
        // The mobile m_list_mutil.js already compresses its upload. This exact
        // desktop-script gate prevents double compression on mobile.
        if (!/\/pc_list_mutil\.js(?:\?|$)/.test(script.src || '')) return;
        vendorScriptSeen = true;
        if (observer) observer.disconnect();
        script.addEventListener('load', activateDesktopListOptimizer, { once: true });
        window.setTimeout(activateDesktopListOptimizer, 0);
      });
    }

    Array.prototype.forEach.call(document.scripts || [], inspectNode);
    if (!vendorScriptSeen) {
      observer = new MutationObserver(function (records) {
        records.forEach(function (record) {
          Array.prototype.forEach.call(record.addedNodes, inspectNode);
        });
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  if (pageType === 'product') installProductOptimizer();
  if (pageType === 'collection' || pageType === 'search') installCollectionOptimizer();
}());
